import { Trans } from '@lingui/react';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Dialog, DialogTitle } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { DataGrid } from '@mui/x-data-grid';
import { ResponsiveGridColDef } from 'components/DataGrid';
import { PAGINATION_PAGE_SIZES } from 'config';
import { i18n } from 'i18n';
import {
  AutoCreatedPourDto,
  ProjectDto,
  useAutoCreatedPoursQuery,
  useUndoAutoPoursMutation
} from 'providers/api';
import { useConfirmation } from 'providers/confirm';
import { usePage } from 'providers/page';
import { useScenario } from 'modules/project/providers/scenario';
import React from 'react';
import { Model } from '../hooks/useModelState';

interface AutoPourListMenuProps {
  model: Model;
  pour: AutoCreatedPourDto;
}
const AutoPourListMenu = ({ pour, model }: AutoPourListMenuProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const undoAutoPours = useUndoAutoPoursMutation();
  const confirm = useConfirmation();

  const handleDelete = (autoPour: AutoCreatedPourDto) => {
    confirm({
      variant: 'danger',
      description: i18n._(
        'Are you sure you want to remove all selected automatically created pours?',
      ),
    }).then(() => {
      undoAutoPours.mutate({
        projectId: model.project.entityId as string,
        autoPourBatchId: autoPour.autoPourBatchId,
      });
    });
  };

  return (
    <>
      <IconButton
        color="inherit"
        aria-controls={`pour-actions-menu-${pour.entityId}`}
        aria-haspopup="true"
        onClick={handleClick}
        size="large"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id={`pour-actions-menu-${pour.entityId}`}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={() => handleDelete(pour)}>
          <ListItemIcon>
            <DeleteForeverIcon color="error" fontSize="small" />
          </ListItemIcon>
          <Typography color="error" variant="inherit">
            <Trans id="Delete">Delete</Trans>
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

interface AutoPourListProps {
  model: Model;
  project: ProjectDto;
  onComplete: () => void;
}

const AutoPourList = ({
  model,
  project,
  onComplete,
}: AutoPourListProps) => {
  const handleCloseModal = () => {
    onComplete();
  };
  const {
    actions: {
      setUndoActionOverride,
      clearUndoActionOverride,
    },
  } = usePage();
  const { activeScenarioId } = useScenario();

  const columns: ResponsiveGridColDef[] = [
    {
      field: 'pourNames',
      headerName: i18n._('Pour Name'),
      flex: 1,
      resizable: false,
      sortable: false,
      filterable: false,
      renderCell: ({ value }) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.5' }}>
          {value}
        </div>
      ),
      mobileConfig: {
        width: 200,
        isVisible: true,
      },
    },
    {
      field: 'stopeNames',
      headerName: i18n._('Stopes'),
      flex: 1,
      resizable: false,
      sortable: false,
      filterable: false,
      renderCell: ({ value }) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.5' }}>
          {value}
        </div>
      ),
      mobileConfig: {
        width: 200,
        isVisible: true,
      },
    },
    {
      field: 'creationDate',
      headerName: i18n._('Created On'),
      width: 200,
      resizable: false,
      sortable: false,
      filterable: false,
      mobileConfig: {
        width: 200,
        isVisible: true,
      },
      valueFormatter: (value: string | Date | undefined) => {
        if (!value) return '';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
          return 'Invalid Date';
        }
        return date.toLocaleString();
      },
    },
    {
      field: '',
      sortable: false,
      filterable: false,
      resizable: false,
      width: 80,
      renderCell: ({ row }) => (
        <AutoPourListMenu
          pour={(row as any) as AutoCreatedPourDto}
          model={model}
        />
      ),
    },
  ];

  const {
    data: pours,
    refetch: refetchAutoPours,
  } = useAutoCreatedPoursQuery({ projectId: project.entityId, scenarioId: activeScenarioId ?? undefined });
  const undoAutoPours = useUndoAutoPoursMutation();
  const undoAutoPoursRef = React.useRef(undoAutoPours);
  undoAutoPoursRef.current = undoAutoPours;

  const latestBatchId = React.useMemo(() => {
    if (!pours || pours.length === 0) {
      return null;
    }

    return pours.reduce((maxBatchId, pour) => (
      (pour.autoPourBatchId ?? 0) > maxBatchId ? (pour.autoPourBatchId ?? 0) : maxBatchId
    ), pours[0].autoPourBatchId ?? 0);
  }, [pours]);

  const handleGlobalUndo = React.useCallback(() => {
    if (!latestBatchId) {
      return;
    }

    setUndoActionOverride((current) => (current ? {
      ...current,
      enabled: false,
      loading: true,
    } : current));

    undoAutoPoursRef.current.mutate(
      {
        projectId: project.entityId,
        autoPourBatchId: latestBatchId,
      },
      {
        onSuccess: async () => {
          clearUndoActionOverride();
          await refetchAutoPours();
        },
        onError: () => {
          setUndoActionOverride({
            enabled: true,
            loading: false,
            onClick: handleGlobalUndo,
          });
        },
      },
    );
  }, [clearUndoActionOverride, latestBatchId, project.entityId, refetchAutoPours, setUndoActionOverride]);

  React.useEffect(() => {
    if (!latestBatchId) {
      setUndoActionOverride({
        enabled: false,
        loading: false,
        onClick: null,
      });
    } else {
      setUndoActionOverride({
        enabled: true,
        loading: false,
        onClick: handleGlobalUndo,
      });
    }

    return () => {
      clearUndoActionOverride();
    };
  }, [clearUndoActionOverride, handleGlobalUndo, latestBatchId, setUndoActionOverride]);

  const rows = pours?.map((pour) => ({
    ...pour,
    id: pour.entityId,
  })) ?? [];
  const isAutoPour = true;

  return (
    <Dialog
      title={i18n._('Automatically Created Pours')}
      open={isAutoPour}
      onClose={handleCloseModal}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle id="simple-dialog-title"><Trans id="Automatically Created Pours">Automatically Created Pours</Trans></DialogTitle>
      <Box sx={{
        m: 2,
      }}
      >
        <DataGrid
          disableColumnMenu
          autoHeight
          rows={rows}
          columns={columns}
          pagination
          pageSizeOptions={PAGINATION_PAGE_SIZES}
        />
      </Box>
    </Dialog>
  );
};

export default AutoPourList;
